import React, { FC, useState } from 'react';
import InputField from '@components/FormFields/InputField/InputField';
import Spacer from '@components/Spacer';
import CheckBox from '@components/FormFields/CheckBox';
import { DocumentRequestFileIntegrationFileNames, DocumentRequestFileIntegrations, DocumentRequestUserFile } from '@/tsClient/servers/noodleDocuments/document-api-models';
import { m } from 'framer-motion';
import CustomSelect from '../FormFields/CustomSelect';

export type Document = {
  description: string;
  id: string;
  isCollapsed: boolean;
  isRequired: boolean;
  title: string;
  order: number;
  isForYourEyesOnly: boolean;
  internalDataType: 'income-data' | 'asset-data' | 'personal-data' | null;
  internalDataPromptHelp: string | null;
  integrationSlug: DocumentRequestFileIntegrations | null;
  integrationFileName: DocumentRequestFileIntegrationFileNames | null;
  metadata: DocumentRequestUserFile['metadata'];
};

const internalDataOptions= [
  {
    title: 'Income Data',
    value: 'income-data',
  },
  {
    title: 'Asset Data',
    value: 'asset-data',
  },
  {
    title: 'Personal Data',
    value: 'personal-data',
  },
];

const integrationSlugOptions = [
  { title: 'Pacer', value: 'pacer' },
];

const integrationFileNameOptions = [
  { title: 'debtor.txt', value: 'debtor.txt' },
  { title: 'petition.pdf', value: 'petition.pdf' },
  { title: 'creditor.txt', value: 'creditor.txt' },
  { title: 'credit.pdf', value: 'credit.pdf' },
];

type Props = {
  document: Document;
  handleChange: (key: string, value: string | boolean | null) => void;
};

const DocumentForm: FC<Props> = ({ document, handleChange }) => {
  const [showIntegrationDetails, setShowIntegrationDetails] = useState(Boolean(document.integrationSlug || document.integrationFileName));

  return (
    <m.div layout initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}>
      <InputField
        id="title"
        name="title"
        values={{ title: document.title }}
        onChange={(val) => handleChange('title', val)}
        label="Title of document"
        hasFixedHeight
      />
      <InputField
        id="description"
        name="description"
        rows={3}
        values={{ description: document.description }}
        onChange={(val) => handleChange('description', val)}
        label="Description"
      />
      <Spacer size={16} />
      <CheckBox
        id={`${document.id}isRequired`}
        name={`${document.id}isRequired`}
        values={{ [`${document.id}isRequired`]: document.isRequired }}
        onChange={() => handleChange('isRequired', !document.isRequired)}
        label={'Required'}
        isSwitch
        isSwitchGreenStyle
        hasFixedHeight={false}
      />
      <CheckBox
        id={`${document.id}isForYourEyesOnly`}
        name={`${document.id}isForYourEyesOnly`}
        values={{ [`${document.id}isForYourEyesOnly`]: document.isForYourEyesOnly }}
        onChange={() => handleChange('isForYourEyesOnly', !document.isForYourEyesOnly)}
        label={`Document will only be visible to your team and whoever uploads this document.`}
        isSwitch
        isSwitchGreenStyle
        hasFixedHeight={false}
      />
      <CheckBox
        id={`${document.id}isMarkedForInternalData`}
        name={`${document.id}isMarkedForInternalData`}
        values={{ [`${document.id}isMarkedForInternalData`]: !!document.internalDataType }}
        onChange={() => handleChange('internalDataType', document.internalDataType ? false : 'income-data')}
        label={`Mark as internal data source`}
        isSwitch
        isSwitchGreenStyle
        hasFixedHeight={false}
      />
      <CheckBox
        id={`${document.id}showIntegrationDetails`}
        name={'showIntegrationDetails'}
        values={{ showIntegrationDetails }}
        onChange={() => {
          handleChange('integrationSlug', null);
          handleChange('integrationFileName', null);
          setShowIntegrationDetails((prev) => !prev);
        }}
        label={'Set integration'}
        isSwitch
        isSwitchGreenStyle
        hasFixedHeight={false}
      />
      {showIntegrationDetails && (
        <>
          <Spacer size={4}/>
          <CustomSelect
            id='integrationSlug'
            label='Select integration'
            value={document.integrationSlug ?? ''}
            options={integrationSlugOptions}
            onChange={(newType) => handleChange('integrationSlug', newType)}
          />
          <Spacer size={8}/>
          <CustomSelect
            id='integrationFileName'
            label='Select integration file'
            value={document.integrationFileName ?? ''}
            options={integrationFileNameOptions}
            onChange={(newType) => handleChange('integrationFileName', newType)}
          />
        </>
      )}
      {document.internalDataType
      && <CustomSelect
        id='internalDataType'
        label='Select internal data type'
        value={document.internalDataType ?? ''}
        options={internalDataOptions}
        onChange={(newType) => handleChange('internalDataType', newType)}
      />
      }
    </m.div>
  );
};

export default DocumentForm;
